export const UNIT_SYSTEMS = {
  METRIC: 'metric',
  IMPERIAL: 'imperial',
};

const CM_TO_FT   = 0.0328084;
const FT_TO_CM   = 30.48;
const KG_TO_LB   = 2.20462;
const LB_TO_KG   = 0.453592;
const CM2_TO_FT2 = 0.00107639;
const CM3_TO_FT3 = 0.0000353147;

export const cmToFt  = (cm) => (cm == null || cm === '') ? null : Number(cm) * CM_TO_FT;
export const ftToCm  = (ft) => (ft == null || ft === '') ? null : Number(ft) * FT_TO_CM;
export const kgToLb  = (kg) => (kg == null || kg === '') ? null : Number(kg) * KG_TO_LB;
export const lbToKg  = (lb) => (lb == null || lb === '') ? null : Number(lb) * LB_TO_KG;
export const cm2ToFt2 = (cm2) => (cm2 == null || cm2 === '') ? null : Number(cm2) * CM2_TO_FT2;
export const cm3ToFt3 = (cm3) => (cm3 == null || cm3 === '') ? null : Number(cm3) * CM3_TO_FT3;

// ── Unit labels ────────────────────────────────────────────────────────────

export const getLengthUnit = (unitSystem) => unitSystem === UNIT_SYSTEMS.IMPERIAL ? 'ft' : 'cm';
export const getWeightUnit = (unitSystem) => unitSystem === UNIT_SYSTEMS.IMPERIAL ? 'lbs' : 'kg';
export const getAreaUnit   = (unitSystem) => unitSystem === UNIT_SYSTEMS.IMPERIAL ? 'ft\u00B2' : 'cm\u00B2';
export const getVolumeUnit = (unitSystem) => unitSystem === UNIT_SYSTEMS.IMPERIAL ? 'ft\u00B3' : 'cm\u00B3';

// ── Canonical conversion ───────────────────────────────────────────────────

export const convertLengthToCanonical   = (v, u) => (v == null || v === '') ? null : u === UNIT_SYSTEMS.IMPERIAL ? ftToCm(v) : Number(v);
export const convertWeightToCanonical   = (v, u) => (v == null || v === '') ? null : u === UNIT_SYSTEMS.IMPERIAL ? lbToKg(v) : Number(v);
export const convertLengthFromCanonical = (v, u) => (v == null || v === '') ? null : u === UNIT_SYSTEMS.IMPERIAL ? cmToFt(v) : Number(v);
export const convertWeightFromCanonical = (v, u) => (v == null || v === '') ? null : u === UNIT_SYSTEMS.IMPERIAL ? kgToLb(v) : Number(v);

// ── Format: primary value + secondary in brackets ─────────────────────────

export const formatLength = (lengthCm, unitSystem, decimals = 2) => {
  if (lengthCm == null || lengthCm === '') return '';
  const value = unitSystem === UNIT_SYSTEMS.IMPERIAL ? cmToFt(lengthCm) : Number(lengthCm);
  return value.toFixed(decimals);
};

export const formatWeight = (weightKg, unitSystem, decimals = 1) => {
  if (weightKg == null || weightKg === '') return '';
  const value = unitSystem === UNIT_SYSTEMS.IMPERIAL ? kgToLb(weightKg) : Number(weightKg);
  return value.toFixed(decimals);
};

export const formatArea = (lengthCm, widthCm, unitSystem, decimals = 2) => {
  if (!lengthCm || !widthCm) return '';
  const areaCm2 = Number(lengthCm) * Number(widthCm);
  const value = unitSystem === UNIT_SYSTEMS.IMPERIAL ? cm2ToFt2(areaCm2) : areaCm2;
  return value.toFixed(decimals);
};

export const formatVolume = (lengthCm, widthCm, heightCm, unitSystem, decimals = 2) => {
  if (!lengthCm || !widthCm || !heightCm) return '';
  const volumeCm3 = Number(lengthCm) * Number(widthCm) * Number(heightCm);
  const value = unitSystem === UNIT_SYSTEMS.IMPERIAL ? cm3ToFt3(volumeCm3) : volumeCm3;
  return value.toFixed(decimals);
};

// ── Dual display: primary (secondary) ────────────────────────────────────
// e.g. "5,000 kg (11,023 lbs)" or "11,023 lbs (5,000 kg)"

export const formatWeightBoth = (weightKg, unitSystem, decimals = 1) => {
  if (weightKg == null || weightKg === '') return '';
  if (unitSystem === UNIT_SYSTEMS.IMPERIAL) {
    const lbs = kgToLb(weightKg).toFixed(decimals);
    const kg  = Number(weightKg).toFixed(decimals);
    return `${lbs} lbs (${kg} kg)`;
  } else {
    const kg  = Number(weightKg).toFixed(decimals);
    const lbs = kgToLb(weightKg).toFixed(decimals);
    return `${kg} kg (${lbs} lbs)`;
  }
};

export const formatLengthBoth = (lengthCm, unitSystem, decimals = 2) => {
  if (lengthCm == null || lengthCm === '') return '';
  if (unitSystem === UNIT_SYSTEMS.IMPERIAL) {
    const ft = cmToFt(lengthCm).toFixed(decimals);
    const cm = Number(lengthCm).toFixed(0);
    return `${ft} ft (${cm} cm)`;
  } else {
    const cm = Number(lengthCm).toFixed(0);
    const ft = cmToFt(lengthCm).toFixed(decimals);
    return `${cm} cm (${ft} ft)`;
  }
};

export const formatAreaBoth = (lengthCm, widthCm, unitSystem, decimals = 2) => {
  if (!lengthCm || !widthCm) return '';
  const areaCm2 = Number(lengthCm) * Number(widthCm);
  if (unitSystem === UNIT_SYSTEMS.IMPERIAL) {
    return `${cm2ToFt2(areaCm2).toFixed(decimals)} ft\u00B2 (${areaCm2.toFixed(0)} cm\u00B2)`;
  } else {
    return `${areaCm2.toFixed(0)} cm\u00B2 (${cm2ToFt2(areaCm2).toFixed(decimals)} ft\u00B2)`;
  }
};

export const formatVolumeBoth = (lengthCm, widthCm, heightCm, unitSystem, decimals = 2) => {
  if (!lengthCm || !widthCm || !heightCm) return '';
  const vol = Number(lengthCm) * Number(widthCm) * Number(heightCm);
  if (unitSystem === UNIT_SYSTEMS.IMPERIAL) {
    return `${cm3ToFt3(vol).toFixed(decimals)} ft\u00B3 (${vol.toFixed(0)} cm\u00B3)`;
  } else {
    return `${vol.toFixed(0)} cm\u00B3 (${cm3ToFt3(vol).toFixed(decimals)} ft\u00B3)`;
  }
};

// ── Preference storage ────────────────────────────────────────────────────

export const getDefaultUnitSystem = () => {
  return localStorage.getItem('preferredUnitSystem') || UNIT_SYSTEMS.IMPERIAL;
};

export const setDefaultUnitSystem = (unitSystem) => {
  localStorage.setItem('preferredUnitSystem', unitSystem);
  // Dispatch event so components can react without a full page reload
  window.dispatchEvent(new CustomEvent('unitSystemChanged', { detail: unitSystem }));
};
